import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Package, MessageSquare, Settings, 
  HelpCircle, LogOut, User, Bell, Menu, X, ChevronRight,
  ShoppingBag, AlertCircle, FileText
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import Chatbot from './Chatbot';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardLayout = ({ children, currentPage }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [currentPage]);

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/auth/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Package, label: 'My Projects', path: '/dashboard/projects', badge: dashboardData?.stats?.total_orders || 0 },
    { icon: ShoppingBag, label: 'Orders', path: '/dashboard/orders' },
    { icon: FileText, label: 'Invoices', path: '/dashboard/invoices' },
    { icon: MessageSquare, label: 'Messages', path: '/dashboard/messages', badge: 0 },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
    { icon: HelpCircle, label: 'Help & Support', path: '/dashboard/support' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-dark)' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#3B82F6]/30 border-t-[#3B82F6] rounded-full animate-spin"></div>
          <div className="text-white/60">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-dark)' }}>
      {/* Top Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 h-16 bg-[#12121A]/95 backdrop-blur-xl border-b border-white/10 z-50">
        <div className="flex items-center justify-between h-full px-4 lg:px-6">
          {/* Left: Logo & Menu Toggle */}
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
              className="lg:hidden text-white hover:text-[#3B82F6] transition-colors p-2 rounded-lg hover:bg-white/5"
            >
              {mobileSidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            
            {/* Desktop sidebar toggle */}
            <button
              data-testid="sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:flex text-white hover:text-[#3B82F6] transition-colors p-2 rounded-lg hover:bg-white/5"
            >
              {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            
            <h1 
              className="text-xl font-bold text-white cursor-pointer"
              onClick={() => navigate('/')}
            >
              CLOUD{' '}
              <span className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] bg-clip-text text-transparent">
                IT
              </span>
            </h1>
          </div>

          {/* Right: Notifications & Profile */}
          <div className="flex items-center gap-3 lg:gap-6">
            {/* Verification Banner - Desktop only */}
            {user && !user.is_verified && (
              <div className="hidden md:flex items-center gap-2 bg-[#F97316]/10 text-[#F97316] px-4 py-2 text-sm border border-[#F97316]/30 rounded-lg">
                <AlertCircle size={16} />
                <span>Please verify your email</span>
                <button className="text-[#FB923C] hover:text-white underline ml-2 font-medium">
                  Resend
                </button>
              </div>
            )}

            {/* Notifications */}
            <button className="relative text-white/60 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/5">
              <Bell size={20} />
              <span className="absolute top-1 right-1 bg-gradient-to-r from-[#F97316] to-[#FB923C] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                0
              </span>
            </button>

            {/* Profile */}
            <div 
              className="flex items-center gap-3 cursor-pointer group p-2 rounded-lg hover:bg-white/5 transition-colors"
              onClick={() => navigate('/dashboard/settings')}
            >
              <div className="w-9 h-9 bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] rounded-full flex items-center justify-center overflow-hidden">
                {user?.profile_photo ? (
                  <img src={user.profile_photo} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <User size={18} className="text-white" />
                )}
              </div>
              <div className="hidden md:block">
                <div className="text-white text-sm font-medium">{user?.name}</div>
                <div className="text-white/50 text-xs capitalize">{user?.user_type} Account</div>
              </div>
              <ChevronRight size={16} className="hidden md:block text-white/40 group-hover:text-[#3B82F6] transition-colors" />
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] bg-[#12121A] border-r border-white/10 transition-all duration-300 z-40 overflow-y-auto
          ${mobileSidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full w-64'} 
          lg:translate-x-0 ${sidebarOpen ? 'lg:w-64' : 'lg:w-0 lg:overflow-hidden'}`}
      >
        <div className="p-4 space-y-1 w-64">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = currentPage === item.path || currentPage?.startsWith(item.path + '/');
            return (
              <button
                key={index}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => {
                  navigate(item.path);
                  setMobileSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                  isActive
                    ? 'bg-gradient-to-r from-[#3B82F6]/20 to-[#8B5CF6]/10 text-[#3B82F6] border-l-4 border-[#3B82F6]'
                    : 'text-white/60 hover:bg-white/5 hover:text-white border-l-4 border-transparent'
                }`}
              >
                <Icon size={20} />
                <span className="flex-1 text-left whitespace-nowrap font-medium">{item.label}</span>
                {item.badge > 0 && (
                  <span className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white text-xs px-2 py-0.5 rounded-full font-bold">
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}

          {/* Divider */}
          <div className="h-px bg-white/10 my-4"></div>

          {/* Logout Button */}
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-xl transition-all duration-300 border-l-4 border-transparent hover:border-red-500"
          >
            <LogOut size={20} />
            <span className="whitespace-nowrap font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main 
        className={`pt-16 min-h-screen transition-all duration-300 
          ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-0'}`}
      >
        <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
          {children}
        </div>
      </main>

      {/* Chatbot */}
      <Chatbot />
    </div>
  );
};

export default DashboardLayout;
